import { shownParams } from "../calc-result";
/**
 * performanceReport
 *
 * This class generates and manages performance reports for helicopter operations,
 * specifically evaluating HOGE (Hover Out of Ground Effect) and CAT-A VTOL (Vertical Takeoff and Landing) conditions.
 *
 * Features:
 * - Creates detailed Markdown and HTML reports for a given flight, including payload margins and performance warnings.
 * - Retrieves environmental and aircraft performance data from backend systems.
 * - Embeds SVG diagram references with annotated performance points (takeoff and landing).
 * - Highlights weight violations with clear visual indicators and warnings.
 * - Supports integration with FPP (Flight Performance Provider) for diagram generation.
 *
 * Typical Use Case:
 * Used within flight planning and operational control tools to evaluate and document aircraft performance
 * under different mission types and environmental conditions.
 *
 *  * Main Functions:
 *
 * - createPerformanceMarkdown(...):
 *   Generates a detailed Markdown report summarizing helicopter performance, weight margins,
 *   environmental conditions, and safety warnings for departure and destination.
 *
 * - getPerformanceHtmlReport(...):
 *   Converts the stored Markdown performance report to HTML using a Markdown processor,
 *   optionally writes to a file when executed on a server.
 *
 * - getProcessResultState(...):
 *   Returns a string representing the processing status of a performance report,
 *   such as "OK", "WARN", "FAIL", or "N/A".
 *
 * - getPerfDiagramData(...):
 *   Produces structured diagram data (lines and annotation points) for SVG performance
 *   charts, including evaluations of weight margins for HOGE and CAT-A VTOL conditions.
 *
 */

/* eslint-disable no-case-declarations */
import { opsCntrolCom } from "../com";
import {
  noOption,
  noSolutionAvail,
  opsMissionTypeEnum,
  perfResultsEnum,
  property,
  targetPlatformEnum,
} from "../dataStruct";
import {
  alignMarkdownTablesInDocument,
  getParameterOfArray,
  getParametersArray,
  getUtcTime,
  kelvin2centigrade,
  markDownProcessing,
  meters2feet,
  padLeft,
  pascal2mbar,
  svgDiagramDataStruct,
  svgPointStruct,
} from "../functions";
import { round } from "./navLogReport";
import { aircraftStruct, fppProvider } from "./providers/fppProvider";

export class performanceReport {
  async createCatASegmentOneMarkdown(
    acft: aircraftStruct,
    classCom: opsCntrolCom,
    takeOffMass: number,
    landingMass: number,
    originOat: number,
    destOat: number,
    depPa: number,
    destPa: number
  ): Promise<string> {
    const fpp = new fppProvider();
    const resultType = perfResultsEnum.cat_a_climboutSegOne_height_over_100ft;
    const lines = ["# CAT-A Segment 1", "Climb-out height gain over 100 ft — source performance chart.", ""];
    if (!(await fpp.isPerfModelAvailable(acft.model, resultType, classCom))) return "";
    lines.push(
      "| LOC | PA | OAT | MASS | HEIGHT GAIN (ft / 100 ft) |",
      "|-----|----|-----|------|---------------------------|"
    );
    const conditions = [
      ["{originIcao}", takeOffMass, originOat, depPa],
      ["{destIcao}", landingMass, destOat, destPa],
    ] as [string, number, number, number][];
    // Origin and destination lookups are independent -- on the web each is
    // its own request, so start both together instead of one after another.
    // Each gets a no-op `.catch` so a rejection of the first is never
    // reported as unhandled while the second is still pending; a rejection
    // still surfaces at the `await` below, in condition order.
    const lookups = conditions.map(([, mass, oat, pa]) =>
      fpp.getPerfModelDiagram(
        acft.model,
        resultType,
        classCom,
        ["outsideAirTemp", "pressureAltitude", "grossWeight"],
        [oat, pa, mass],
        [],
        true,
        acft.perfParameters
      )
    );
    lookups.forEach((lookup) => lookup.catch(() => {}));
    for (const [index, [loc, mass, oat, pa]] of conditions.entries()) {
      const results = await lookups[index];
      const result = results[0];
      const valid =
        result &&
        Number.isFinite(result.value) &&
        !result._violation &&
        result.value !== noOption &&
        result.value !== noSolutionAvail;
      // The model stores height in metres; the source chart reports feet gained per 100 ft horizontal.
      const heightGain = valid ? round(result.value / 0.3048, 2).toFixed(2) : "N/A";
      lines.push(
        `|${loc}|${round(meters2feet(pa), 0)} ft|${round(kelvin2centigrade(oat), 1)} °C|${round(mass, 1)} kg|${heightGain}|`
      );
    }
    lines.push("", `[]fppDiagr:${acft.model}:${resultType}`, "");
    return lines.join("\n");
  }

  async createPerformanceMarkdown(
    acft: aircraftStruct,
    flightId: string,
    opsMissionType: opsMissionTypeEnum,
    classCom: opsCntrolCom,
    takeOffMass: number,
    landingMass: number,
    origin_elevation: number,
    dest_elevation: number,
    origin_oat: number,
    dest_oat: number,
    origin_qnh: number,
    dest_qnh: number,
    depPressureAltitude: number,
    destPressureAltitude: number,
    depHeliHogeMass: number,
    destHeliHogeMass: number,
    depHeliHogeMassLeft: number,
    destHeliHogeMassLeft: number,
    depHeliVtolCatAMass: number,
    destHeliVtolCatAMass: number,
    depHeliVtolCatAMassLeft: number,
    destHeliVtolCatAMassLeft: number,
    filterConditions_hoga?: string[],
    filterConditions_cata?: string[],
    estLandingCenterOfGravityX?: number,
    desEstimatedHoistMass?: number,
    aeoHogeMarkdown: string = "",
    segmentOneStarted?: Promise<string>
  ): Promise<string> {
    const flightlistParams = await getParametersArray(flightId, "flightlist", opsMissionTypeEnum.all);

    const mdText_lst: string[] = [];
    let mdString = "";

    try {
      // The segment one lookups and the hoist availability read depend on
      // nothing the report builds, so they start with the first wave and are
      // awaited where the report needs them.
      const fpp = new fppProvider();
      const segmentOneRead =
        segmentOneStarted ??
        this.createCatASegmentOneMarkdown(
          acft,
          classCom,
          takeOffMass,
          landingMass,
          origin_oat,
          dest_oat,
          depPressureAltitude,
          destPressureAltitude
        );
      segmentOneRead.catch(() => undefined);
      const hoistratingRead = fpp.isPerfModelAvailable(
        acft.model,
        perfResultsEnum.maxWinchMassLimit_preloading,
        classCom
      );
      hoistratingRead.catch(() => undefined);
      const flightRowRead = classCom.namedQuery("flightlistByFlightId", { flightId });
      flightRowRead.catch(() => undefined);
      const revisionRowRead = classCom.namedQuery("fuelCountByFlightId", { flightId });
      revisionRowRead.catch(() => undefined);

      // ======================
      // Header - General Flight Info
      // ======================
      mdText_lst.push("# PERFORMANCE REPORT");
      mdText_lst.push(
        "Flight **{FlightNumber}** -  Ops Mission Type: **{opsMissionType}** - revision {revision} @  {issueTime} \n"
      );
      mdText_lst.push(
        "ETD **{estTimeDep_gui_UTC} / {estTimeDep_gui_LT}**:   **{originName}** to **{destName}**,  alternate {altName}\n"
      );
      mdText_lst.push("_____________\n");

      mdText_lst.push(" ");
      // ======================
      // Hover Ceiling (HOGE) Section
      // ======================
      mdText_lst.push(aeoHogeMarkdown);
      mdText_lst.push("# HOGE — One Engine Inoperative (OEI)");
      mdText_lst.push("30-second power, heating off. Informational only; does not trigger PERF FAIL.\n");
      mdText_lst.push("| LOC | ELEV | QNH | PA | OAT | TOM | MX HOGE MASS | MX HOGE PAYLOAD TO MTOM |");
      mdText_lst.push("|-----|------|-----|----|-----|-----|--------------|--------------------|");
      mdText_lst.push(
        "|{originIcao}|{origin_elevation}|{origin_qnh}|{depPressureAltitude}|{origin_oat}|{takeOffMass}|{depHeliHogeMass}|{depHeliHogeMassLeft}|"
      );
      mdText_lst.push(
        "|{destIcao}|{dest_elevation}|{dest_qnh}|{destPressureAltitude}|{dest_oat}|{landingMass}|{destHeliHogeMass}|{destHeliHogeMassLeft}|"
      );
      mdText_lst.push(" ");
      mdText_lst.push(
        "[]fppDiagr:" + acft.model + ":" + perfResultsEnum.hoge + ":" + JSON.stringify(filterConditions_hoga) + "\n"
      );

      // ======================
      // CAT-A VTOL Section
      // ======================
      mdText_lst.push("# CAT-A Vertical Takeoff/Landing");
      mdText_lst.push("### MTOM and LDM CAT-A (VTOL)");
      mdText_lst.push("| LOC | ELEV | QNH | PA | OAT | TOM | MX CAT-A MASS | MX CAT-A PAYLOAD TO MTOM |");
      mdText_lst.push("|-----|------|-----|----|-----|-----|--------------|--------------------|");
      mdText_lst.push(
        "|{originIcao}|{origin_elevation}|{origin_qnh}|{depPressureAltitude}|{origin_oat}|{takeOffMass}|{depHeliVtolCatAMass}|{depHeliVtolCatAMassLeft}|"
      );
      mdText_lst.push(
        "|{destIcao}|{dest_elevation}|{dest_qnh}|{destPressureAltitude}|{dest_oat}|{landingMass}|{destHeliVtolCatAMass}|{destHeliVtolCatAMassLeft}|"
      );
      mdText_lst.push(" ");
      mdText_lst.push("|>");
      mdText_lst.push(
        "[]fppDiagr:" +
          acft.model +
          ":" +
          perfResultsEnum.cat_a_vtol_maxGrossMass +
          ":" +
          JSON.stringify(filterConditions_cata)
      );
      mdText_lst.push("|<\n");

      mdText_lst.push(await segmentOneRead);

      // ======================
      // Rated Hoist Load Section
      // ======================

      // hoist rating calculations
      const hoistratingAvail: boolean = await hoistratingRead;

      if (hoistratingAvail) {
        // === Estimated Rated Hoist Load at Destination (RHL) ===

        // calculate the host load for destination

        if (desEstimatedHoistMass !== noOption) {
          mdText_lst.push("# Estimated Rated Hoist Load @dest {destIcao}");

          mdText_lst.push("");
          mdText_lst.push("## HOIST PERFORMANCE");

          mdText_lst.push(""); // spacer before box
          mdText_lst.push("PARAMETER                 | VALUE (UNIT)");
          mdText_lst.push("--------------------------| --------------------");
          mdText_lst.push(`Landing Mass              | ${landingMass} kg`);
          mdText_lst.push(`Longitudinal CG (X)       | ${estLandingCenterOfGravityX} m`);
          mdText_lst.push(" "); // spacer before box

          mdText_lst.push("[]fppDiagr:" + acft.model + ":" + perfResultsEnum.maxWinchMassLimit_preloading);
          mdText_lst.push("");
        }
      }

      // ======================
      // Fetch dynamic data from DB & utils
      // ======================
      const [flightNumber, revisionRaw, altIcao, originIcao, destIcao] = await Promise.all([
        (await flightRowRead)[0].flightNumber,
        (await revisionRowRead)[0],
        getParameterOfArray(flightId, "flightlist", property.icaoAlternate, flightlistParams)[0]?.value ?? "",
        getParameterOfArray(flightId, "flightlist", property.icaoOrigin, flightlistParams)[0]?.value ?? "",
        getParameterOfArray(flightId, "flightlist", property.icaoDest, flightlistParams)[0]?.value ?? "",
      ]);

      const revision = String(revisionRaw.n);

      const utcTimeNow = getUtcTime(new Date());
      const issueTime = `${padLeft(utcTimeNow.getHours().toString(), "0", 2)}:${padLeft(
        utcTimeNow.getMinutes().toString(),
        "0",
        2
      )} UTC`;

      const estTimeDepRaw = new Date(
        Number(getParameterOfArray(flightId, "flightlist", property.estTimeDep, flightlistParams)[0]?.value ?? 0)
      );
      const estTimeDep_gui_LT = `${padLeft(estTimeDepRaw.getHours().toString(), "0", 2)}:${padLeft(
        estTimeDepRaw.getMinutes().toString(),
        "0",
        2
      )} LT`;
      const estTimeDep_gui_UTC = `${padLeft(getUtcTime(estTimeDepRaw).getHours().toString(), "0", 2)}:${padLeft(
        getUtcTime(estTimeDepRaw).getMinutes().toString(),
        "0",
        2
      )} UTC`;

      // ======================
      // Replace Template Values
      // ======================
      const replaceMap: Record<string, string> = {
        "{FlightNumber}": flightNumber,
        "{opsMissionType}": opsMissionTypeEnum[opsMissionType].toUpperCase(),
        "{revision}": revision,
        "{issueTime}": issueTime,
        "{estTimeDep_gui_UTC}": estTimeDep_gui_UTC,
        "{estTimeDep_gui_LT}": estTimeDep_gui_LT,
        "{originName}": originIcao,
        "{destName}": destIcao,
        "{altName}": altIcao,
        "{takeOffMass}": `${round(takeOffMass, 0)} kg`,
        "{landingMass}": `${round(landingMass, 0)} kg`,
        "{origin_oat}": `${kelvin2centigrade(origin_oat)} °C`,
        "{dest_oat}": `${kelvin2centigrade(dest_oat)} °C`,
        "{origin_qnh}": `${pascal2mbar(origin_qnh)} hPA`,
        "{dest_qnh}": `${pascal2mbar(dest_qnh)} hPA`,
        "{origin_elevation}": `${meters2feet(origin_elevation)} ft`,
        "{dest_elevation}": `${meters2feet(dest_elevation)} ft`,
        "{depPressureAltitude}": `${meters2feet(depPressureAltitude)} ft`,
        "{destPressureAltitude}": `${meters2feet(destPressureAltitude)} ft`,
        "{originIcao}": originIcao,
        "{destIcao}": destIcao,
        "{depHeliHogeMass}": `${round(depHeliHogeMass, 0)} kg`,
        "{destHeliHogeMass}": `${round(destHeliHogeMass, 0)} kg`,
        "{depHeliHogeMassLeft}": `${round(depHeliHogeMassLeft, 0)} kg`,
        "{destHeliHogeMassLeft}": `${round(destHeliHogeMassLeft, 0)} kg`,
        "{depHeliVtolCatAMass}": `${round(depHeliVtolCatAMass, 0)} kg`,
        "{destHeliVtolCatAMass}": `${round(destHeliVtolCatAMass, 0)} kg`,
        "{depHeliVtolCatAMassLeft}": `${round(depHeliVtolCatAMassLeft, 0)} kg`,
        "{destHeliVtolCatAMassLeft}": `${round(destHeliVtolCatAMassLeft, 0)} kg`,
        "{desEstimatedHoistMass}": `${round(desEstimatedHoistMass, 0)} kg`,
        "{estLandingCenterOfGravityX}": `${round(estLandingCenterOfGravityX, 3)} m`,
        [noSolutionAvail.toString()]: "__(no solution found)__",
      };

      // Perform Replacements
      for (const [key, value] of Object.entries(replaceMap)) {
        mdText_lst.forEach((line, index) => {
          mdText_lst[index] = line.replaceAll(key, value);
        });
      }

      // Join all markdown lines into one string
      mdString = mdText_lst.join("\n");

      return alignMarkdownTablesInDocument(mdString);
    } catch (err) {
      console.log("WARN: could not create perf report! #345213 " + err!);
      return " could not create perf report! #345213! + err";
    }
  }

  async getPerformanceHtmlReport(
    flightId: string,
    opsMissionType: opsMissionTypeEnum,
    classCom: any,
    returnMarkdown: boolean = false
  ): Promise<string> {
    const result = await classCom.namedQuery("perfProcessReportByFlight", { flightId, opsMissionType });

    const mdp = new markDownProcessing();

    if (result.length === 0) {
      // No live perf row. Since `initPerformance` refuses to run without a live
      // mass and balance, an empty read here is the expected shape of that
      // refusal, not a fault — and the two callers that open this report
      // (`mission-briefing-panel`, `ops-control.service`) swallow a throw, so
      // dereferencing `result[0]` made the button silently do nothing. Say why
      // instead. Markdown either way; the caller decides whether to convert.
      const reason =
        "## Performance\n\nNo performance calculation for this flight.\n\n" +
        "The mass and balance did not complete, so the performance step was not run " +
        "(it will not compute against a missing mass). Re-run the flight calculation " +
        "once the mass and balance succeeds.\n";

      return returnMarkdown
        ? reason
        : await mdp.convMarkdown(reason, classCom.appearance, flightId, opsMissionType, classCom, this);
    }

    if (returnMarkdown) {
      return result[0].processReport;
    }

    const html: string = await mdp.convMarkdown(
      result[0].processReport,
      classCom.appearance,
      flightId,
      opsMissionType,
      classCom,
      this,
      result[0].signatureId,
      result[0].resultJson
    );

    if (classCom.targetPlatform == targetPlatformEnum.server) {
      const fs = classCom.package_fs;
      fs.writeFile("out.html", html, function (err) {
        if (!err) {
          const open = classCom.package_open;
          open("out.html");
        }
      });
    }

    return html;
  }

  async getProcessResultState(flightId: string, opsMissionType: opsMissionTypeEnum, classCom: any): Promise<string> {
    const result = await classCom.namedQuery("perfProcessResultStateByFlight", { flightId, opsMissionType });

    if (result.length == 0) {
      return "NIL";
    }

    if (result[0].processResultState == 0) {
      return "N/A";
    }

    if (result[0].processResultState == 1) {
      return "OK";
    }

    if (result[0].processResultState == 2) {
      return "WARN";
    }

    if (result[0].processResultState == 3) {
      return "FAIL";
    }

    return "N/A";
  }

  async getPerfDiagramData(
    perfResult: perfResultsEnum,
    acftModel: string,
    classCom: any,
    flightId: string,
    opsMissionType: opsMissionTypeEnum,
    filterConditions: string[],
    perfParamters: string,
    resultJson?: string
  ): Promise<svgDiagramDataStruct[]> {
    // The figures of the report version that is shown. Its document holds the
    // values of both parameter scopes and the masses it took from the loading.
    const perfAllParams = await shownParams(
      resultJson,
      (result) => result.values,
      () => getParametersArray(flightId, "perf", opsMissionTypeEnum.all)
    );
    const perfMissionParams = await shownParams(
      resultJson,
      (result) => result.values,
      () => getParametersArray(flightId, "perf", opsMissionType)
    );
    // A document without the masses (written before they were stored) reads
    // the loading parameters.
    const loadingParams = await shownParams(
      resultJson,
      (result) =>
        result.inputs.takeOffMass === undefined
          ? null
          : {
              [property.takeOffMass]: result.inputs.takeOffMass,
              [property.estLandingMass]: result.inputs.landingMass,
              [property.estLandingCenterOfGravityX]: result.inputs.landingCgX,
              [property.desEstimatedHoistMass]: result.values[property.desEstimatedHoistMass],
            },
      () => getParametersArray(flightId, "loading", opsMissionType)
    );

    const oatOrigin = Number(getParameterOfArray(flightId, "perf", property.oatOrigin, perfAllParams)[0]?.value ?? 0);
    const oatDest = Number(getParameterOfArray(flightId, "perf", property.oatDest, perfAllParams)[0]?.value ?? 0);
    const depPressureAltitude = Number(
      getParameterOfArray(flightId, "perf", property.depPressureAltitude, perfAllParams)[0]?.value ?? 0
    );
    const destPressureAltitude = Number(
      getParameterOfArray(flightId, "perf", property.destPressureAltitude, perfAllParams)[0]?.value ?? 0
    );

    const offsetX = 180; // offset to avoid label overlap
    const fpp = new fppProvider();

    switch (perfResult) {
      case perfResultsEnum.cat_a_climboutSegOne_height_over_100ft: {
        const takeOffMass = Number(
          getParameterOfArray(flightId, "loading", property.takeOffMass, loadingParams)[0]?.value ?? 0
        );
        const landingMass = Number(
          getParameterOfArray(flightId, "loading", property.estLandingMass, loadingParams)[0]?.value ?? 0
        );
        // Keep each location's source chart separate; do not interpret its output as a mass margin.
        const takeoff = await fpp.getPerfModelDiagram(
          acftModel,
          perfResult,
          classCom,
          ["outsideAirTemp", "pressureAltitude", "grossWeight"],
          [oatOrigin, depPressureAltitude, takeOffMass],
          ["@t/o"],
          false,
          perfParamters
        );
        const landing = await fpp.getPerfModelDiagram(
          acftModel,
          perfResult,
          classCom,
          ["outsideAirTemp", "pressureAltitude", "grossWeight"],
          [oatDest, destPressureAltitude, landingMass],
          ["@ldg"],
          false,
          perfParamters
        );
        for (const [diagrams, location] of [
          [takeoff, "@t/o"],
          [landing, "@ldg"],
        ] as [svgDiagramDataStruct[], string][]) {
          for (const diagram of diagrams) {
            diagram._diagram_txtDescription = location + " " + (diagram._diagram_txtDescription || "");
            if (
              !Number.isFinite(diagram.value) ||
              diagram._violation ||
              diagram.value === noOption ||
              diagram.value === noSolutionAvail
            )
              continue;
            const label = `${location} ${round(diagram.value / 0.3048, 2).toFixed(2)} ft / 100 ft`;
            const backgroundColor = diagram.value < 0 ? "#ff0000" : "#00ff00";
            if (diagram.points?.length) {
              for (const point of diagram.points) {
                point.desig = label;
                point.backgroundColor = backgroundColor;
                point.fontColor = "#000000";
              }
            } else {
              // Segment 1 exports trace lines without point overlays. Mark the end of the result trace.
              const trace = diagram.lines?.filter((line) => line.points?.length).slice(-1)[0];
              const position = trace?.points.slice(-1)[0];
              if (position)
                diagram.points = [{ position: [...position], desig: label, backgroundColor, fontColor: "#000000" }];
            }
          }
        }
        return [...takeoff, ...landing];
      }
      // ================================
      // HOGE
      // ================================
      case perfResultsEnum.hoge: {
        const depLeft = Number(
          getParameterOfArray(flightId, "perf", property.depHeliHogeMassLeft, perfMissionParams)[0]?.value ?? 0
        );
        const destLeft = Number(
          getParameterOfArray(flightId, "perf", property.destHeliHogeMassLeft, perfMissionParams)[0]?.value ?? 0
        );

        const labelOrigin = depLeft < 0 ? `${round(depLeft, 0)} kg` : `+${round(depLeft, 0)} kg`;
        const labelDest = destLeft < 0 ? `${round(destLeft, 0)} kg` : `+${round(destLeft, 0)} kg`;

        const lookupLanding = await fpp.getPerfModelDiagram(
          acftModel,
          perfResult,
          classCom,
          ["outsideAirTemp", "pressureAltitude"],
          [oatDest, destPressureAltitude],
          [labelDest],
          false,
          perfParamters,
          filterConditions
        );

        const lookupTakeoff = await fpp.getPerfModelDiagram(
          acftModel,
          perfResult,
          classCom,
          ["outsideAirTemp", "pressureAltitude"],
          [oatOrigin, depPressureAltitude],
          [labelOrigin],
          false,
          perfParamters,
          filterConditions
        );

        return this.mergePerfDiagrams(
          lookupTakeoff,
          lookupLanding,
          "@t/o " + labelOrigin,
          "@ldg " + labelDest,
          depLeft,
          destLeft,
          offsetX
        );
      }

      // ================================
      // CAT-A VTOL
      // ================================
      case perfResultsEnum.cat_a_vtol_maxGrossMass: {
        const depLeft = Number(
          getParameterOfArray(flightId, "perf", property.depHeliVtolCatAMassLeft, perfMissionParams)[0]?.value ?? 0
        );
        const destLeft = Number(
          getParameterOfArray(flightId, "perf", property.destHeliVtolCatAMassLeft, perfMissionParams)[0]?.value ?? 0
        );

        const labelOrigin = depLeft < 0 ? `${round(depLeft, 0)} kg` : `+${round(depLeft, 0)} kg`;
        const labelDest = destLeft < 0 ? `${round(destLeft, 0)} kg` : `+${round(destLeft, 0)} kg`;

        const lookupLanding = await fpp.getPerfModelDiagram(
          acftModel,
          perfResult,
          classCom,
          ["outsideAirTemp", "pressureAltitude"],
          [oatDest, destPressureAltitude],
          [labelDest],
          false,
          perfParamters,
          filterConditions
        );

        const lookupTakeoff = await fpp.getPerfModelDiagram(
          acftModel,
          perfResult,
          classCom,
          ["outsideAirTemp", "pressureAltitude"],
          [oatOrigin, depPressureAltitude],
          [labelOrigin],
          false,
          perfParamters,
          filterConditions
        );

        return this.mergePerfDiagrams(
          lookupTakeoff,
          lookupLanding,
          "@t/o " + labelOrigin,
          "@ldg " + labelDest,
          depLeft,
          destLeft,
          offsetX
        );
      }

      // ================================
      // Rated Hoist Load (RHL)
      // ================================
      case perfResultsEnum.maxWinchMassLimit_preloading: {
        const estLandingCenterOfGravityXVal = getParameterOfArray(
          flightId,
          "loading",
          property.estLandingCenterOfGravityX,
          loadingParams
        )[0]?.value;
        if (estLandingCenterOfGravityXVal === undefined) {
          console.log("warn: skipping maxWinchMassLimit_preloading — loading data not yet computed");
          break;
        }
        const estLandingMass = Number(
          getParameterOfArray(flightId, "loading", property.estLandingMass, loadingParams)[0]?.value ?? 0
        );
        const estLandingCenterOfGravityX = Number(estLandingCenterOfGravityXVal);
        const desEstimatedHoistMass = Number(
          getParameterOfArray(flightId, "loading", property.desEstimatedHoistMass, loadingParams)[0]?.value ?? 0
        );

        try {
          return await fpp.getPerfModelDiagram(
            acftModel,
            perfResult,
            classCom,
            ["centerOfGravityLon", "grossWeight"],
            [estLandingCenterOfGravityX, estLandingMass],
            [desEstimatedHoistMass + " kg est H-Load @dest"],
            false,
            perfParamters
          );
        } catch {
          console.log("warn: no solution to be found maxWinchMassLimit_preloading");
        }
        break;
      }

      default:
        console.log("warn: perf-result-type not defined! " + perfResultsEnum[perfResult]);
    }
  }

  private mergePerfDiagrams(
    takeoff: svgDiagramDataStruct[],
    landing: svgDiagramDataStruct[],
    labelOrigin: string,
    labelDest: string,
    originMassLeft: number,
    destMassLeft: number,
    offsetX: number
  ): svgDiagramDataStruct[] {
    for (let i = 0; i < landing.length; i++) {
      takeoff[i].points = [];
      landing[i].points = [];

      let lines = takeoff[i].lines ?? landing[i].lines;
      if (!lines) return landing;

      for (let j = 0; j < lines.length; j++) {
        if (landing[i].lines && takeoff[i].lines) {
          landing[i].lines.push(takeoff[i].lines[j]);
        }

        if (j === 0) {
          if (takeoff[i].lines) {
            const p1 = new svgPointStruct();
            const pos = takeoff[i].lines[1].points[1];
            p1.position = [pos[0], pos[1]];
            p1.desig = labelOrigin;
            p1.backgroundColor = originMassLeft < 0 ? "#ff0000" : "#00ff00";
            p1.fontColor = "#000000;";
            takeoff[i].points.push(p1);
            landing[i].points.push(p1);
          }

          if (landing[i].lines) {
            const p2 = new svgPointStruct();
            const pos = landing[i].lines[1].points[1];
            p2.position = [pos[0] + offsetX, pos[1]];
            p2.desig = labelDest;
            p2.backgroundColor = destMassLeft < 0 ? "#ff0000" : "#00ff00";
            p2.fontColor = "#000000;";
            landing[i].points.push(p2);
          }
        }
      }
    }

    return landing[0]._violation ? takeoff : landing;
  }
}
